import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Mail, FileText, Package } from "lucide-react";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  return (
    <div data-testid="order-success-page" className="max-w-3xl mx-auto px-6 py-16 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-blue-600" strokeWidth={1.5}/>
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
        Thank you. Your order has been submitted.
      </h1>
      <p className="mt-3 text-slate-600 max-w-xl mx-auto">
        We'll review your research request and send an invoice shortly. Look out for a confirmation email at the address you provided.
      </p>

      {order && (
        <div className="mt-8 max-w-md mx-auto border border-slate-200 rounded-md bg-white text-left">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Order</div>
              <div data-testid="success-order-id" className="font-mono text-sm text-slate-900">{order.order_id}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Status</div>
              <div className="text-sm text-blue-700 font-medium">{order.status}</div>
            </div>
          </div>
          <div className="px-5 py-3 border-b border-slate-100">
            {order.items?.map((it, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1">
                <div>{it.name}{it.variant_label ? ` · ${it.variant_label}` : ""} × {it.quantity}</div>
                <div className="font-mono">${(Number(it.unit_price) * Number(it.quantity)).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Total</div>
            <div data-testid="success-total" className="font-mono text-lg font-semibold">${Number(order.total).toFixed(2)}</div>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-2xl mx-auto">
        <Info icon={Mail} title="Watch your inbox" description="You'll receive an order confirmation email, followed by an invoice within 1–2 business days."/>
        <Info icon={FileText} title="Invoice next" description="We'll email a printable invoice with payment instructions."/>
        <Info icon={Package} title="Then we ship" description="After payment, we prepare and ship. Track everything from your dashboard."/>
      </div>

      <div className="mt-10 flex gap-3 justify-center">
        <Link to={`/dashboard/orders`}><Button className="bg-slate-900 hover:bg-blue-600 text-white">View my orders</Button></Link>
        <Link to="/catalog"><Button variant="outline" className="border-slate-300">Continue browsing</Button></Link>
      </div>
    </div>
  );
}

function Info({ icon: I, title, description }) {
  return (
    <div className="border border-slate-200 rounded-md p-4 bg-white">
      <I className="w-5 h-5 text-blue-600" strokeWidth={1.5}/>
      <div className="mt-3 font-display text-sm font-semibold">{title}</div>
      <p className="mt-1 text-xs text-slate-600 leading-relaxed">{description}</p>
    </div>
  );
}
